// ============================================================================
// LES CONDITIONS D'UN FOURNISSEUR — ce qui s'accepte, écrit une fois pour le serveur et pour l'écran.
//
// Remise, port, escompte, compte fournisseur, adresse et e-mail de commande vivent dans une table du
// SERVEUR (`supplier_terms`) : `supplier` descend sur chaque caisse, et y ranger la comptabilité ou les
// conditions négociées d'un client les ferait voyager sur toute la flotte.
//
// ⚠️ REFUSER, JAMAIS CORRIGER : une remise de 101 %, un e-mail sans domaine ou un compte en minuscules
//    sont refusés avec leur motif. Les ramener « à peu près » enregistrerait une valeur que personne n'a
//    tapée, et la commande partirait avec.
// ⚠️ UNE SEULE RÈGLE pour les deux côtés : l'écran la lit pour prévenir pendant la saisie, le producteur
//    la rejoue avant d'écrire au journal. Deux copies finiraient par accepter d'un côté ce que l'autre
//    refuse.
// ⚠️ LA RÈGLE REND UN CODE, PAS UNE PHRASE : l'écran se traduit, le serveur parle français. Une phrase
//    française rendue par la règle et glissée dans un texte d'écran resterait française pour tous les
//    comptes, et aucun contrôle de traduction ne pourrait la réclamer.
// ============================================================================

/** Les champs des conditions, tels que l'écran et le producteur les échangent. `null` = vidé. */
export interface SupplierTermsFields {
  /** Remise fournisseur, en points de base (2,5 % = 250). Pré-remplit chaque nouvelle commande. */
  discountBp?: number | null;
  /** Frais de port, en centimes HT. Ajoutés à une commande qui porte au moins une pièce. */
  shippingCents?: number | null;
  /** Escompte pour paiement anticipé, en points de base. Ne touche jamais le prix d'achat. */
  earlyPaymentBp?: number | null;
  /** Délai de paiement qui ouvre l'escompte, en jours. */
  earlyPaymentDays?: number | null;
  /** Compte du fournisseur chez le cabinet (`401TEXNORD`). */
  accountCode?: string | null;
  address1?: string | null;
  address2?: string | null;
  postalCode?: string | null;
  city?: string | null;
  country?: string | null;
  /** Adresse qui recevra le bon de commande. */
  orderEmail?: string | null;
}

export type SupplierTermsField = keyof SupplierTermsFields;

/** Pourquoi une valeur est refusée — l'écran et le serveur en font chacun leur phrase. */
export type SupplierTermsRefusal = 'rate' | 'shipping' | 'days' | 'account' | 'postal_chars' | 'postal_length' | 'text_length' | 'email';

/** Bornes de saisie, nommées pour que le message et le contrôle disent la même chose. */
export const SUPPLIER_TERMS_LIMITS = {
  maxShippingCents: 10_000_000,
  maxEarlyPaymentDays: 365,
  maxTextLength: 120,
  maxPostalCodeLength: 10,
} as const;

/**
 * Une adresse e-mail plausible : un nom, une arobase, un domaine qui a un point.
 * ⚠️ Plausible seulement : la seule preuve qu'une adresse reçoit est un envoi, et c'est la commande
 *    envoyée qui la donnera. Cette règle écarte les fautes de frappe, pas les boîtes mortes.
 */
export function isEmailAddress(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value.trim());
}

/** Compte fournisseur : chiffres et majuscules, 17 caractères au plus — la forme d'un compte auxiliaire. */
export const SUPPLIER_ACCOUNT_RE = /^[0-9A-Z]{1,17}$/;

/**
 * Ce que l'écran dit sous le compte saisi.
 * ⚠️ `unusual` n'est PAS un refus : un cabinet peut numéroter ses fournisseurs autrement, et c'est lui
 *    qui fait foi. L'écran le signale, il ne l'interdit pas.
 */
export function supplierAccountHint(code: string | null | undefined): 'empty' | 'unusual' | 'ok' {
  const c = (code ?? '').trim();
  if (c === '') return 'empty';
  return c.startsWith('401') ? 'ok' : 'unusual';
}

const isWhole = (v: unknown): v is number => typeof v === 'number' && Number.isInteger(v);

/**
 * Le refus d'UN champ — `null` s'il est acceptable. `null` et la chaîne vide ne sont jamais refusés :
 * un champ vidé n'est pas une erreur.
 */
export function supplierTermsFieldRefusal(field: SupplierTermsField, value: unknown): SupplierTermsRefusal | null {
  if (value == null || value === '') return null;
  switch (field) {
    case 'discountBp':
    case 'earlyPaymentBp':
      return isWhole(value) && value >= 0 && value <= 10_000 ? null : 'rate';
    case 'shippingCents':
      return isWhole(value) && value >= 0 && value <= SUPPLIER_TERMS_LIMITS.maxShippingCents ? null : 'shipping';
    case 'earlyPaymentDays':
      return isWhole(value) && value >= 1 && value <= SUPPLIER_TERMS_LIMITS.maxEarlyPaymentDays ? null : 'days';
    case 'accountCode':
      return typeof value === 'string' && SUPPLIER_ACCOUNT_RE.test(value) ? null : 'account';
    case 'postalCode':
      if (typeof value !== 'string' || !/^[0-9A-Za-z -]+$/.test(value)) return 'postal_chars';
      return value.length <= SUPPLIER_TERMS_LIMITS.maxPostalCodeLength ? null : 'postal_length';
    case 'orderEmail':
      return typeof value === 'string' && isEmailAddress(value) ? null : 'email';
    case 'address1':
    case 'address2':
    case 'city':
    case 'country':
      return typeof value === 'string' && value.length <= SUPPLIER_TERMS_LIMITS.maxTextLength ? null : 'text_length';
  }
}

/** Le nom français d'un champ, pour la phrase du serveur. */
const FIELD_TEXT: Record<SupplierTermsField, string> = {
  discountBp: 'remise fournisseur', shippingCents: 'frais de port', earlyPaymentBp: 'escompte',
  earlyPaymentDays: 'délai d’escompte', accountCode: 'compte fournisseur', address1: 'adresse',
  address2: 'complément d’adresse', postalCode: 'code postal', city: 'ville', country: 'pays',
  orderEmail: 'e-mail de commande',
};

/** Ce que chaque refus exige, en français — la phrase du serveur. */
const REFUSAL_TEXT: Record<SupplierTermsRefusal, string> = {
  rate: 'un pourcentage entre 0 et 100, deux décimales au plus',
  shipping: 'un montant positif en euros, deux décimales au plus',
  days: `un nombre de jours entre 1 et ${SUPPLIER_TERMS_LIMITS.maxEarlyPaymentDays}`,
  account: 'chiffres et lettres majuscules, 17 caractères au plus',
  postal_chars: 'chiffres, lettres, espaces et tirets seulement',
  postal_length: `${SUPPLIER_TERMS_LIMITS.maxPostalCodeLength} caractères au plus`,
  text_length: `${SUPPLIER_TERMS_LIMITS.maxTextLength} caractères au plus`,
  email: 'adresse incomplète — la commande ne pourrait pas lui être envoyée',
};

/**
 * Les motifs de refus d'une saisie de conditions, en français, pour le producteur — vide si elle passe.
 * Un champ absent (`undefined`) n'est pas jugé : la saisie ne porte que ce qui a changé.
 */
export function supplierTermsRefusals(f: SupplierTermsFields): string[] {
  const out: string[] = [];
  for (const field of Object.keys(FIELD_TEXT) as SupplierTermsField[]) {
    const v = f[field];
    const refusal = supplierTermsFieldRefusal(field, v);
    if (refusal) out.push(`${FIELD_TEXT[field]} « ${String(v)} » : ${REFUSAL_TEXT[refusal]}`);
  }
  return out;
}
